/**
 * CampaignTriggerBadge - Trigger type indicator for campaigns
 *
 * Displays campaign trigger with semantic color coding:
 * - signup: blue (on account signup)
 * - trial_end: amber (before trial ends)
 * - plan_change: purple (on plan upgrade/downgrade)
 * - inactivity: red (on account inactivity)
 * - manual: slate (manually triggered)
 */
import { UserPlus, Clock, RefreshCw, AlertCircle, Hand } from 'lucide-react';
import { cn } from '@respectly/ui';
import type { CampaignTrigger } from '@respectly/shared-types';

// ============================================
// Types
// ============================================

interface CampaignTriggerBadgeProps {
  trigger: CampaignTrigger;
  showIcon?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

// ============================================
// Configuration
// ============================================

const triggerConfig: Record<
  CampaignTrigger,
  { label: string; icon: typeof UserPlus; dotColor: string; badgeClass: string }
> = {
  signup: {
    label: 'Signup',
    icon: UserPlus,
    dotColor: 'bg-blue-500',
    badgeClass:
      'bg-blue-500/10 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400 border-blue-500/20',
  },
  trial_end: {
    label: 'Trial Ending',
    icon: Clock,
    dotColor: 'bg-amber-500',
    badgeClass:
      'bg-amber-500/10 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400 border-amber-500/20',
  },
  plan_change: {
    label: 'Plan Change',
    icon: RefreshCw,
    dotColor: 'bg-purple-500',
    badgeClass:
      'bg-purple-500/10 text-purple-700 dark:bg-purple-500/20 dark:text-purple-400 border-purple-500/20',
  },
  inactivity: {
    label: 'Inactivity',
    icon: AlertCircle,
    dotColor: 'bg-red-500',
    badgeClass:
      'bg-red-500/10 text-red-700 dark:bg-red-500/20 dark:text-red-400 border-red-500/20',
  },
  manual: {
    label: 'Manual',
    icon: Hand,
    dotColor: 'bg-slate-500',
    badgeClass:
      'bg-slate-500/10 text-slate-600 dark:bg-slate-500/20 dark:text-slate-400 border-slate-500/20',
  },
};

const sizeClasses = {
  sm: 'px-1.5 py-0.5 text-[10px]',
  md: 'px-2 py-0.5 text-xs',
  lg: 'px-2.5 py-1 text-sm',
};

const iconSizeClasses = {
  sm: 'h-3 w-3',
  md: 'h-3.5 w-3.5',
  lg: 'h-4 w-4',
};

// ============================================
// Component
// ============================================

export function CampaignTriggerBadge({
  trigger,
  showIcon = true,
  size = 'md',
  className,
}: CampaignTriggerBadgeProps) {
  const config = triggerConfig[trigger];
  const Icon = config.icon;

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border font-medium',
        sizeClasses[size],
        config.badgeClass,
        className
      )}
    >
      {showIcon && <Icon className={iconSizeClasses[size]} />}
      {config.label}
    </span>
  );
}

// ============================================
// Utility Functions
// ============================================

export function getTriggerColor(trigger: CampaignTrigger): string {
  const colors: Record<CampaignTrigger, string> = {
    signup: '#3b82f6',
    trial_end: '#f59e0b',
    plan_change: '#a855f7',
    inactivity: '#ef4444',
    manual: '#64748b',
  };
  return colors[trigger];
}

export function getTriggerLabel(trigger: CampaignTrigger): string {
  return triggerConfig[trigger].label;
}

export function getTriggerIcon(trigger: CampaignTrigger) {
  return triggerConfig[trigger].icon;
}
